import { Suspense, lazy, startTransition, useEffect, useMemo, useState, type ReactNode } from "react";
import { RadarChart } from "./components/RadarChart";
import { ReadinessDial } from "./components/ReadinessDial";
import { JourneyRail } from "./components/JourneyRail";
import { TracePreviewChip } from "./components/TracePreviewChip";
import { IntakeStep } from "./steps/IntakeStep";
import { ConsentStep } from "./steps/ConsentStep";
import { DarkRoomStep } from "./steps/DarkRoomStep";
import { WhatIfPanel } from "./steps/WhatIfPanel";
import { ReleaseRoomStep } from "./steps/ReleaseRoomStep";
import { browserState, loadJson, saveJson, loadPreference, savePreference } from "./utils";
import { LocalDataControls } from "./components/LocalDataControls";
import { AssessmentRunStatus } from "./components/AssessmentRunStatus";
import { createLocalRunRecord, resolveWorkspaceContext, type LocalRunRecord } from "../domain/localRun";
import routeManifest from "./route-manifest.json";
import type { View, Theme } from "./types";
import { OBELISK_TRANSITION_STORAGE_KEY, type ObeliskTransitionSession } from "../obelisk-callback";
import type { AdminQueueAction, AdminQueueItem, ArtistIntake, ConsentAcceptance, DarkRoomReview } from "../domain/schemas";
import { routeDarkRoomLite } from "../domain/aiRouter";
import { createMockIntake, runDarkRoomLiteMock, scoreEvidenceQuality, type EvidenceQualityReport } from "../domain/darkRoom";
import { buildPublicAnalyticsRequest } from "../domain/analytics";
import { createPreparationPreview } from "../domain/assessmentPreview";
import { buildSignalDecisions } from "../domain/decisions";
import { buildDecisionSources, type DecisionSourceTrace } from "../domain/decisionSources";
import { DecisionSummary } from "./components/DecisionSummary";
import { ProjectActions } from "./components/ProjectActions";
import { ProjectHistorySync } from "./components/ProjectHistorySync";
const LazyProjectHistory = lazy(async () => ({ default: (await import("./components/ProjectHistory")).ProjectHistory }));
const LazyPreparationProgress = lazy(async () => ({ default: (await import("./components/PreparationProgress")).PreparationProgress }));
const LazyReportSources = lazy(async () => ({ default: (await import("./components/ReportSources")).ReportSources }));
import { buildShadowSignalReport } from "../domain/report";
import { comparableAssessmentSuffix, type AssessmentEnvelope } from "../domain/assessment";
import { encodeSharedReceiptToken, freshIntakeFromReceipt, importedReceiptMethod, parseSignalReceipt, RECEIPT_TRUST_STATEMENT } from "../domain/receiptImport";
import {
  buildAnalyticsEvent,
  buildReadinessMap,
  buildReleasePackageDraft,
  buildSignalCapsule,
  buildSignalCapsuleReceipt,
  buildShadowActionPlan,
  buildSignalDigestDraft,
  buildStudioGateBrief,
  computePersonaTensions,
  computePersonaGenreDeltas,
  computePersistentBlockers,
  computeReadinessVelocity,
  getPersonaReactionLine,
  buildArtistArchetypeProfile,
  computeScoreAnatomy,
  buildAxisConsensusMatrix,
  depthTiers,
  journeySteps,
  recommendedDepthIndex,
  summarizeSignalReceipt,
  type JourneyStep,
  type ShadowAnalyticsEventName,
  type ShadowSignalCapsuleReceipt,
  type ShadowReceiptContinuitySummary
} from "../domain/product";


interface DeliveryApiResponse {
  error?: string;
  message?: string;
  waitlistStatus?: string;
  deliveryStatus?: string;
  emailStatus?: string;
  toEmail?: string;
  receiptId?: string;
}

async function readJsonSafe<T>(response: Response): Promise<T | null> {
  try {
    return await response.json() as T;
  } catch {
    return null;
  }
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function generateArtistFingerprint(artistName: string, scores: Record<string, number>): string {
  let hash = 0;
  for (let i = 0; i < artistName.length; i++) hash = ((hash << 5) - hash + artistName.charCodeAt(i)) | 0;
  const scoreVals = Object.values(scores);
  const scoreSum = scoreVals.reduce((a, b) => a + b, 0);
  const W = 280, H = 56, bars = 48;
  const barW = W / bars;
  let paths = "";
  for (let i = 0; i < bars; i++) {
    const seed = (hash ^ (i * 2654435761)) >>> 0;
    const scoreInfluence = scoreVals[i % scoreVals.length] / 100;
    const amplitude = (0.3 + scoreInfluence * 0.7) * (H / 2 - 4);
    const h = amplitude * (0.4 + ((seed & 0xff) / 255) * 0.6);
    const x = i * barW + barW * 0.15;
    const cy = H / 2;
    const color = scoreSum / scoreVals.length >= 70 ? "#6246ea" : scoreSum / scoreVals.length >= 55 ? "#7c5cbf" : "#e74c3c";
    const opacity = 0.4 + ((seed >> 8) & 0xff) / 255 * 0.6;
    paths += `<rect x="${x.toFixed(1)}" y="${(cy - h).toFixed(1)}" width="${(barW * 0.7).toFixed(1)}" height="${(h * 2).toFixed(1)}" rx="1.5" fill="${color}" opacity="${opacity.toFixed(2)}"/>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" role="img" aria-label="Artist fingerprint for ${escapeSvgText(artistName)}">${paths}</svg>`;
}

function generatePersonaCard(persona: { persona: string; score: number; verdict: string }, artistName: string): string {
  const scoreColor = persona.score >= 70 ? "#2ecc71" : persona.score >= 50 ? "#7c5cbf" : "#e74c3c";
  const verdictSnip = persona.verdict.length > 80 ? persona.verdict.slice(0, 77) + "…" : persona.verdict;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="480" height="240" viewBox="0 0 480 240">
  <rect width="480" height="240" rx="12" fill="#0d0b09"/>
  <rect width="480" height="240" rx="12" fill="none" stroke="#2a2520" stroke-width="1"/>
  <text x="24" y="38" font-family="Georgia,serif" font-size="11" fill="#6b6560" letter-spacing="0.1em">SHADOW SIGNAL</text>
  <text x="24" y="78" font-family="Georgia,serif" font-size="22" font-weight="700" fill="#f5f1eb">${escapeSvgText(persona.persona)}</text>
  <text x="24" y="106" font-family="Georgia,serif" font-size="13" fill="#9a9490">${escapeSvgText(artistName)}</text>
  <text x="380" y="78" font-family="Georgia,serif" font-size="36" font-weight="700" fill="${scoreColor}" text-anchor="end">${persona.score}</text>
  <line x1="24" y1="122" x2="456" y2="122" stroke="#2a2520" stroke-width="1"/>
  <text x="24" y="148" font-family="Georgia,serif" font-size="12" fill="#c8c2bc" xml:space="preserve">${escapeSvgText(verdictSnip)}</text>
  <text x="24" y="218" font-family="Georgia,serif" font-size="10" fill="#4a4540">VaultSpark Studios · yourshadow.io</text>
</svg>`;
}

const demoIntake: ArtistIntake = {
  artistName: "VOID",
  email: "artist@example.com",
  genre: "dark cinematic hip-hop",
  links: ["https://example.com/void"],
  goals: ["release strategy", "brand clarity"],
  aiComfortLevel: "analysis_only",
  critiqueMode: "direct",
  privacyLevel: "public_only",
  songText: "I kept a room inside the room where nobody could follow."
};

const defaultIntake: ArtistIntake = {
  artistName: "",
  email: "",
  genre: "",
  links: [],
  goals: [],
  aiComfortLevel: "analysis_only",
  critiqueMode: "direct",
  privacyLevel: "public_only",
  songText: ""
};

// Required attestations start unchecked — active-attestation decision 2026-06-21
const defaultConsent: ConsentAcceptance = {
  analysisConsent: false,
  rightsAttestation: false,
  aiDisclosureAcknowledged: false,
  marketingOptIn: false,
  benchmarkOptIn: false,
  version: "shadow-consent-v1"
};

// Demo consent for hero/preview rendering only — never used for artist sessions
const demoConsent: ConsentAcceptance = {
  analysisConsent: true,
  rightsAttestation: true,
  aiDisclosureAcknowledged: true,
  marketingOptIn: false,
  benchmarkOptIn: false,
  version: "shadow-consent-v1"
};

const loadSupportRoutes = () => import("./routes/SupportRoutes");
const loadAdminRoute = () => import("./routes/AdminRoute");
const loadPersonaDeepDiveModal = () => import("./components/PersonaDeepDiveModal");
const loadCoachMarksOverlay = () => import("./components/CoachMarksOverlay");
const loadPackageStep = () => import("./steps/PackageStep");

const LazyCompareRoute = lazy(async () => ({ default: (await loadSupportRoutes()).CompareRoute }));
const LazySharedSignalRoute = lazy(async () => ({ default: (await loadSupportRoutes()).SharedSignalRoute }));
const LazyAuthCallbackRoute = lazy(async () => ({ default: (await loadSupportRoutes()).AuthCallbackRoute }));
const LazyLoginRoute = lazy(async () => ({ default: (await loadSupportRoutes()).LoginRoute }));
const LazyContactRoute = lazy(async () => ({ default: (await loadSupportRoutes()).ContactRoute }));
const LazyPolicyRoute = lazy(async () => ({ default: (await loadSupportRoutes()).PolicyRoute }));
const LazyIpRoute = lazy(async () => ({ default: (await loadSupportRoutes()).IpRoute }));
const LazyArtistsRoute = lazy(async () => ({ default: (await loadSupportRoutes()).ArtistsRoute }));
const LazyAdminRoute = lazy(async () => ({ default: (await loadAdminRoute()).AdminRoute }));
const LazyPersonaDeepDiveModal = lazy(async () => ({ default: (await loadPersonaDeepDiveModal()).PersonaDeepDiveModal }));
const LazyCoachMarksOverlay = lazy(async () => ({ default: (await loadCoachMarksOverlay()).CoachMarksOverlay }));
const LazyPackageStep = lazy(async () => ({ default: (await loadPackageStep()).PackageStep }));

export function App() {
  const [generation, setGeneration] = useState(0);
  const [resetNotice, setResetNotice] = useState("");
  function eraseLocalData() {
    const result = browserState().erase();
    setResetNotice(result.ok
      ? "SHADOW data in this browser was erased. Server records and other browsers are unaffected."
      : "Some browser data could not be erased. Clear this site's data in browser settings before sharing the browser. Server records are unaffected.");
    setGeneration(n => n + 1);
  }
  function startFreshReceiptIntake(receipt: ShadowSignalCapsuleReceipt) {
    const fresh = freshIntakeFromReceipt(receipt);
    const erased = browserState().erase();
    if (erased.ok) {
      saveJson("shadow-intake", fresh.intake);
      saveJson("shadow-consent", fresh.consent);
      savePreference("shadow-journey-step", "intake");
      savePreference("shadow-onboarding-complete", "1");
      savePreference("signalGuideShown", "1");
      setResetNotice("A fresh session-only intake uses the receipt's artist name. Previous SHADOW browser data was erased; all acceptance is unchecked. Server records are unaffected.");
    } else {
      setResetNotice("Some browser data could not be erased. The receipt was not used to start an intake. Clear this site's data in browser settings before sharing the browser; server records are unaffected.");
    }
    setGeneration(n => n + 1);
  }
  return <ArtistWorkspace key={generation} onEraseLocal={eraseLocalData} onStartReceiptIntake={startFreshReceiptIntake} resetNotice={resetNotice} />;
}

function ArtistWorkspace({ onEraseLocal, onStartReceiptIntake, resetNotice }: { onEraseLocal: () => void; onStartReceiptIntake: (receipt: ShadowSignalCapsuleReceipt) => void; resetNotice: string }) {
  const [view, setView] = useState<View>(() => pathToView(window.location.pathname));
  const [theme, setTheme] = useState<Theme>(() => initialTheme());
  const [step, setStep] = useState<JourneyStep>(() => loadJourneyStep());
  const [intake, setIntake] = useState<ArtistIntake>(() => loadJson("shadow-intake", defaultIntake));
  const [consent, setConsent] = useState<ConsentAcceptance>(() => loadJson("shadow-consent", defaultConsent));
  const [waitlistTier, setWaitlistTier] = useState<string | null>(null);
  const [localRunRecord, setLocalRunRecord] = useState<LocalRunRecord | null>(() => loadJson("shadow-assessment-run", null));
  const [runRestored, setRunRestored] = useState(() => localRunRecord !== null);
  const [workspaceContext] = useState(() => resolveWorkspaceContext(loadJson("shadow-workspace-context", null), localRunRecord));
  const [runError, setRunError] = useState("");
  const [analyticsEvents, setAnalyticsEvents] = useState(() => [
    buildAnalyticsEvent("shadow_landing_view", { route: window.location.pathname })
  ]);
  const [review, setReview] = useState<DarkRoomReview>(() => {
    const initialDemoReview: DarkRoomReview = (() => {
      const artist = createMockIntake(demoIntake, demoConsent);
      const demoReview = runDarkRoomLiteMock(artist, demoIntake.songText ?? "");
      demoReview.assessment = { ...demoReview.assessment!, mode: "demo" };
      return demoReview;
    })();
    return localRunRecord?.run.review ?? loadJson("shadow-review", initialDemoReview);
  });

  const [debouncedIntake, setDebouncedIntake] = useState<ArtistIntake>(intake);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedIntake(intake), 300);
    return () => clearTimeout(id);
  }, [intake]);

  const report = useMemo(() => buildShadowSignalReport(review), [review]);

  const returningArtistName = useMemo<string | null>(() => {
    const history = loadJson<Array<{ artistName: string; ts: number }>>("shadow-signal-history", []);
    if (history.length === 0) return null;
    const last = history[history.length - 1];
    return last.artistName ?? null;
  }, []);
  // Local task claims cannot change preparation scores or clear reviewed dependencies.
  const readiness = useMemo(() => buildReadinessMap(review, 0, intake.genre ?? undefined), [intake.genre, review]);
  const decisionSourceResult = useMemo(() => buildDecisionSources(review, localRunRecord), [review, localRunRecord]);
  const decisionSummary = decisionSourceResult.summary;
  const studioGate = useMemo(() => buildStudioGateBrief(review, readiness), [readiness, review]);
  const actionPlan = useMemo(
    () => buildShadowActionPlan(review, readiness, studioGate),
    [readiness, review, studioGate]
  );
  const releasePackage = useMemo(() => buildReleasePackageDraft(review, readiness), [readiness, review]);
  const signalCapsule = useMemo(
    () => buildSignalCapsule(review, readiness),
    [readiness, review]
  );
  const route = useMemo(
    () =>
      routeDarkRoomLite({
        analysisConsent: consent.analysisConsent,
        taskType: "dark_room_lite",
        privacyLevel: intake.privacyLevel,
        paidStatus: "free",
        maxCostUsd: 0.1,
        structuredOutput: true,
        contentSensitivity: consent.rightsAttestation && consent.aiDisclosureAcknowledged ? "standard" : "rights_unclear"
      }),
    [consent.analysisConsent, consent.aiDisclosureAcknowledged, consent.rightsAttestation, intake.privacyLevel]
  );

  const preparationPreview = useMemo(() => createPreparationPreview(debouncedIntake, consent, route), [debouncedIntake, consent, route]);
  const previewPersonas = preparationPreview?.personaReviews;
  const liveReadinessScore = preparationPreview?.compositeScores["readiness"];

  const liveEvidenceQuality = useMemo(() => {
    const songText = debouncedIntake.songText ?? "";
    if (!debouncedIntake.artistName.trim() && !songText.trim()) return null;
    return scoreEvidenceQuality(debouncedIntake, songText);
  }, [debouncedIntake]);

  useEffect(() => {
    if (step === "signal") {
      void loadPersonaDeepDiveModal();
    }
    if (step !== "landing") {
      const warm = window.setTimeout(() => {
        void loadCoachMarksOverlay();
      }, 0);
      return () => window.clearTimeout(warm);
    }
  }, [step]);

  const personaConfidence = useMemo(() => {
    const counts = { high: 0, medium: 0, low: 0 };
    for (const p of review.personaReviews) counts[p.confidence as keyof typeof counts]++;
    return { ...counts, total: review.personaReviews.length, mediumOrAbove: counts.high + counts.medium };
  }, [review]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    savePreference("shadow-theme", theme);
  }, [theme]);

  useEffect(() => {
    savePreference("shadow-journey-step", step);
    saveJson("shadow-intake", intake);
    saveJson("shadow-consent", consent);
    saveJson("shadow-review", review);
    saveJson("shadow-workspace-context", workspaceContext);
    if (localRunRecord) saveJson("shadow-assessment-run", localRunRecord);
  }, [consent, intake, review, step, workspaceContext, localRunRecord]);

  function record(eventName: ShadowAnalyticsEventName, metadata: Record<string, string | number | boolean | undefined> = {}, reference: Pick<DarkRoomReview, "id" | "artistId"> = review) {
    setAnalyticsEvents((current) => [
      ...current.slice(-19),
      buildAnalyticsEvent(eventName, { artistId: reference.artistId, reviewId: reference.id, route: `/${view}`, metadata })
    ]);
  }

  function prefetchView(nextView: View) {
    if (nextView === "admin") {
      void loadAdminRoute();
      return;
    }
    if ((routeManifest.supportChunkViews as View[]).includes(nextView)) {
      void loadSupportRoutes();
    }
  }

  function navigate(nextView: View) {
    const knownPaths = routeManifest.views as Record<View, string>;
    prefetchView(nextView);
    window.history.pushState({}, "", knownPaths[nextView] ?? "/");
    startTransition(() => setView(nextView));
    record(nextView === "artists" ? "shadow_landing_view" : "release_room_opened", { nextView });
  }

  function goto(nextStep: JourneyStep) {
    startTransition(() => setStep(nextStep));
    if (nextStep === "intake") record("shadow_signal_started");
    if (nextStep === "release_room") record("release_room_opened", { readinessScore: readiness.score });
  }

  function update<K extends keyof ArtistIntake>(key: K, value: ArtistIntake[K]) {
    setIntake((current) => ({ ...current, [key]: value }));
  }

  function clearIntake() {
    setIntake(defaultIntake);
    setConsent(defaultConsent);
  }

  function runReview() {
    let nextRecord: LocalRunRecord;
    try { nextRecord = createLocalRunRecord(intake, consent, route, workspaceContext); }
    catch { setRunError("A new report could not be generated. Check your artist inputs, supported text length and acceptance selections. Your previous report is preserved."); setStep("intake"); return; }
    setRunError("");
    setLocalRunRecord(nextRecord);
    setRunRestored(false);
    const nextReview = nextRecord.run.review;
    setReview(nextReview);
    setStep("signal");
    const nextReadiness = buildReadinessMap(nextReview, 0, intake.genre ?? undefined);
    const completionMetadata = {
      genre: intake.genre ?? undefined,
      readinessScore: nextReadiness.score,
      readinessStatus: nextReadiness.status,
      suggestedDepth: nextReview.recommendation.suggestedDepth,
      confidence: nextReview.confidence,
      openGateCount: nextReadiness.milestones.filter((milestone) => milestone.status !== "ready").length
    };
    record("dark_room_lite_generated", { estimatedCostUsd: route.costLedger.estimatedCostUsd }, nextReview);
    record("shadow_signal_completed", completionMetadata, nextReview);
    fetch("/api/shadow/analytics", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(buildPublicAnalyticsRequest("shadow_signal_completed", {
        localReferences: { artistRecordId: nextReview.artistId, runId: nextReview.id },
        route: "/signal",
        metadata: completionMetadata
      }))
    }).catch(() => undefined);

    const overallScore = Object.values(nextReview.compositeScores as Record<string, number>).reduce((a, b) => a + b, 0) /
      Math.max(1, Object.keys(nextReview.compositeScores).length);
    const readinessScore = nextReview.compositeScores["readiness"] as number ?? 0;
    const personaScores: Record<string, number> = {};
    for (const pr of nextReview.personaReviews) personaScores[pr.persona] = pr.score;
    const openGatesEntry = nextReview.recommendation.missingInfo ?? [];
    const historyEntry = { ts: Date.now(), runId: nextReview.id, artistRecordId: nextReview.artistId, projectId: workspaceContext.projectId, assessment: nextReview.assessment, artistName: intake.artistName.trim(), readinessScore: Math.round(readinessScore), overallScore: Math.round(overallScore), personaScores, openGates: openGatesEntry };
    const prevHistory: typeof historyEntry[] = loadJson("shadow-signal-history", []);
    const nextHistory = [...prevHistory, historyEntry].slice(-5);
    saveJson("shadow-signal-history", nextHistory);

    const lastSnap = { intake: JSON.parse(JSON.stringify(intake)), openGates: nextReview.recommendation.missingInfo, readinessScore: Math.round(readinessScore) };
    saveJson("shadow-last-review-snapshot", lastSnap);

    if (consent.benchmarkOptIn) {
      fetch("/api/shadow/analytics", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(buildPublicAnalyticsRequest("benchmark_opted_in", {
          route: "/signal",
          metadata: { genre: intake.genre ?? undefined }
        }))
      }).catch(() => undefined);
    }
  }

  function handleRegisterInterest(tier: string) {
    record("upgrade_clicked", { tier });
    setWaitlistTier(tier);
  }

  const nav = <AppHeader theme={theme} setTheme={setTheme} navigate={navigate} />;
  const footer = <AppFooter navigate={navigate} />;
  const routeFallback = <RouteFallback nav={nav} footer={footer} />;

  if (view === "privacy") return <Suspense fallback={routeFallback}><LazyPolicyRoute title="Privacy" nav={nav} footer={footer} /></Suspense>;
  if (view === "terms") return <Suspense fallback={routeFallback}><LazyPolicyRoute title="Terms" nav={nav} footer={footer} /></Suspense>;
  if (view === "ip") return <Suspense fallback={routeFallback}><LazyIpRoute nav={nav} footer={footer} /></Suspense>;
  if (view === "contact") return <Suspense fallback={routeFallback}><LazyContactRoute nav={nav} footer={footer} /></Suspense>;
  if (view === "compare") return <Suspense fallback={routeFallback}><LazyCompareRoute nav={nav} footer={footer} onBack={() => navigate("app")} /></Suspense>;
  if (view === "share") return <Suspense fallback={routeFallback}><LazySharedSignalRoute nav={nav} footer={footer} /></Suspense>;
  if (view === "auth") return <Suspense fallback={routeFallback}><LazyAuthCallbackRoute nav={nav} footer={footer} onContinue={() => navigate("admin")} /></Suspense>;
  if (view === "login") return <Suspense fallback={routeFallback}><LazyLoginRoute nav={nav} footer={footer} /></Suspense>;
  if (view === "artists") {
    return <Suspense fallback={routeFallback}><LazyArtistsRoute nav={nav} footer={footer} onStart={() => navigate("app")} /></Suspense>;
  }
  if (view === "admin") {
    return (
      <Suspense fallback={routeFallback}>
        <LazyAdminRoute
          nav={nav}
          footer={footer}
          item={adminQueueItemFromSnapshot({ review, report, releaseRoomStatus: readiness.status, studioGate, email: intake.email })}
          analyticsEvents={analyticsEvents}
          obeliskSession={loadObeliskTransitionSession()}
          onBack={() => navigate("app")}
        />
      </Suspense>
    );
  }

  return (
    <main className="shell" id="main-content">
      <a href="#main-content" className="skipLink">Skip to main content</a>
      {waitlistTier && (
        <WaitlistModal
          tier={waitlistTier}
          artistName={intake.artistName}
          artistEmail={intake.email}
          onClose={() => setWaitlistTier(null)}
        />
      )}
      {nav}
      <section className="hero productHero">
        <div>
          {returningArtistName && (
            <div className="returnUserBanner" role="status" aria-live="polite">
              Welcome back, <strong>{returningArtistName}</strong> — your last Signal is saved.{" "}
              <button type="button" className="returnUserContinue" onClick={() => goto("dark_room")}>
                Re-run The Dark Room →
              </button>
            </div>
          )}
          <div className="heroProofBanner" role="list" aria-label="Key features">
            <span className="heroBadge" role="listitem">✦ 10 Dark Room Personas</span>
            <span className="heroBadge" role="listitem">✦ Free · No Account</span>
            <span className="heroBadge" role="listitem">✦ Readiness Score</span>
            <span className="heroBadge" role="listitem">✦ Seamline Draft</span>
            <span className="heroBadge" role="listitem">✦ Studio Gate Brief</span>
          </div>
          <p className="eyebrow">VaultSpark Studios presents</p>
          <h1>Every artist gets a SHADOW.</h1>
          <p className="lede">
            Before a release, every gap matters. SHADOW organizes the evidence you supply into one preparation diagnostic, shown through ten persona lenses. See missing evidence and next actions before a manual release review.
          </p>
          <div className="heroActions">
            <button type="button" onClick={() => goto("intake")}>
              Start Signal
            </button>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                if (step !== "landing") goto("landing");
                setTimeout(() => document.getElementById("signal-preview")?.scrollIntoView({ behavior: "smooth" }), 60);
              }}
            >
              Preview Signal
            </button>
          </div>
        </div>
        <div className="heroSignalDeck" aria-label="SHADOW Signal snapshot">
          <p className="heroAssessmentMethod">{review.assessment?.mode === "demo" ? "Example score — not your assessment" : review.assessment ? `${review.assessment.mode.replaceAll("_", " ")} · preparation coverage` : "Legacy score — method unknown"}</p>
          <ReadinessDial readinessScore={readiness.score} status={readiness.status} />
          <div className="heroSignalMeta">
            <div>
              <span>Dark Room</span>
              <strong>10 personas</strong>
            </div>
            <div>
              <span>Gate posture</span>
              <strong>Manual review</strong>
            </div>
            <div>
              <span>Free tier</span>
              <strong>No live spend</strong>
            </div>
          </div>
        </div>
      </section>

      <JourneyRail current={step} onSelect={goto} />
      <LocalDataControls onErase={onEraseLocal} resetNotice={resetNotice} />
      <ProjectHistorySync record={localRunRecord} />
      {runError && <p className="panel" role="alert">{runError}</p>}

      {step === "landing" && (
        <section id="signal-preview" className="workspace landingWorkspace">
          <article className="panel">
            <p className="panelKicker">How SHADOW works</p>
            <h2>Three steps, zero ambiguity.</h2>
            <div className="howItWorks">
              <div className="howItWorksStep">
                <span>01</span>
                <h3>Artist evidence</h3>
                <p>Your name, genre, goals, public links, and one lyric or hook. Consent gates run first — nothing evaluates without your explicit agreement.</p>
              </div>
              <div className="howItWorksStep">
                <span>02</span>
                <h3>The Dark Room</h3>
                <p>One local preparation rubric organizes supplied text and intake through ten persona lenses. Audio, audience demand and ownership remain unverified.</p>
              </div>
              <div className="howItWorksStep">
                <span>03</span>
                <h3>Signal + Release Room</h3>
                <p>A scored readiness report, next actions ranked by urgency, a Studio Gate brief, and a Seamline draft ready to hand off. Free, no account required.</p>
              </div>
            </div>
          </article>
          <StatusBoard route={route} readiness={readiness} />
        </section>
      )}

      {step === "intake" && <IntakeStep intake={intake} update={update} onClear={clearIntake} onNext={() => goto("consent")} liveReadinessScore={liveReadinessScore} evidenceQuality={liveEvidenceQuality} />}
      {step === "consent" && (
        <ConsentStep consent={consent} setConsent={setConsent} onBack={() => goto("intake")} onRun={() => goto("dark_room")} />
      )}
      {step === "dark_room" && <DarkRoomStep route={route} onRun={runReview} onBack={() => goto("consent")} personaReviews={previewPersonas ?? undefined} />}
      {step === "signal" && (
        <>
        <AssessmentRunStatus record={localRunRecord} review={review} restored={runRestored} />
        <SignalStep
          report={report}
          review={review}
          decisionSummary={decisionSummary}
          decisionSources={decisionSourceResult.traces}
          projectId={localRunRecord?.run.references.projectId ?? null}
          readiness={readiness}
          studioGate={studioGate}
          actionPlan={actionPlan}
          signalCapsule={signalCapsule}
          localRunRecord={localRunRecord}
          personaConfidence={personaConfidence}
          intakeArtistName={intake.artistName}
          artistIntake={intake}
          onReleaseRoom={() => goto("release_room")}
          onPackage={() => goto("package")}
          onRegisterInterest={handleRegisterInterest}
          onStartReceiptIntake={onStartReceiptIntake}
          onCorrectedInputs={inputs => { setIntake(inputs); setConsent({ ...defaultConsent }); goto("intake"); }}
          onFeedback={(verdict) => record("signal_feedback_submitted", { verdict, readinessScore: readiness.score })}
          onPersonaFeedback={(personaSlug, verdict) => record("signal_feedback_submitted", { verdict, personaSlug, readinessScore: readiness.score })}
        />
        </>
      )}
      {step === "release_room" && (
        <ReleaseRoomStep record={localRunRecord} onSignal={() => goto("signal")} onPackage={() => goto("package")} onGoToIntake={() => goto("intake")} />
      )}
      {step === "package" && <Suspense fallback={<section className="workspace"><article className="panel" role="status"><h2>Opening release preparation…</h2><p>Loading the editor for your current workspace.</p></article></section>}><LazyPackageStep record={localRunRecord} releasePackage={releasePackage} report={report} onBack={() => goto("release_room")} /></Suspense>}

      <Suspense fallback={null}>
        <LazyCoachMarksOverlay currentStep={step} />
      </Suspense>

      {footer}
    </main>
  );
}

function RouteFallback({ nav, footer }: { nav: ReactNode; footer: ReactNode }) {
  return (
    <main className="shell" id="main-content">
      {nav}
      <section className="panel">
        <p className="panelKicker">Loading route</p>
        <h2>Opening SHADOW…</h2>
        <p>The next surface is loading as a separate client chunk.</p>
      </section>
      {footer}
    </main>
  );
}

function AppFooter({ navigate }: { navigate: (view: View) => void }) {
  return (
    <footer>
      <span>© 2026 VaultSpark Studios LLC. All rights reserved.</span>
      <nav aria-label="Footer">
        <button type="button" onClick={() => navigate("app")}>Signal</button>
        <button type="button" onClick={() => navigate("artists")}>Artists</button>
        <button type="button" onClick={() => navigate("privacy")}>Privacy</button>
        <button type="button" onClick={() => navigate("terms")}>Terms</button>
        <button type="button" onClick={() => navigate("ip")}>IP</button>
        <button type="button" onClick={() => navigate("contact")}>Contact</button>
        <button type="button" onClick={() => navigate("login")}>Sign in</button>
        <a href="/agents.json">agents.json</a>
        <a href="/.well-known/llms.txt">llms.txt</a>
      </nav>
    </footer>
  );
}

function AppHeader({ theme, setTheme, navigate }: { theme: Theme; setTheme: (theme: Theme) => void; navigate: (view: View) => void }) {
  return (
    <nav className="topNav" aria-label="Primary">
      <button type="button" className="brandButton" onClick={() => navigate("app")}>
        <span>SHADOW</span>
        <small>artist operating system</small>
      </button>
      <div className="navLinks">
        <button type="button" onClick={() => navigate("app")}>Signal</button>
        <button type="button" onClick={() => navigate("artists")}>Artists</button>
        <button type="button" onClick={() => navigate("privacy")}>Privacy</button>
        <button type="button" onClick={() => navigate("terms")}>Terms</button>
        <button type="button" onClick={() => navigate("contact")}>Contact</button>
      </div>
      <div className="themeToggle" aria-label="Theme">
        <button type="button" className={theme === "dark" ? "active" : undefined} aria-pressed={theme === "dark"} onClick={() => setTheme("dark")}>Dark</button>
        <button type="button" className={theme === "light" ? "active" : undefined} aria-pressed={theme === "light"} onClick={() => setTheme("light")}>Light</button>
        <button type="button" className={theme === "eclipse" ? "active" : undefined} aria-pressed={theme === "eclipse"} onClick={() => setTheme("eclipse")}>Eclipse</button>
      </div>
    </nav>
  );
}

function SignalStep({
  report,
  review,
  decisionSummary,
  decisionSources,
  projectId,
  readiness,
  studioGate,
  actionPlan,
  signalCapsule,
  localRunRecord,
  personaConfidence,
  intakeArtistName,
  artistIntake,
  onReleaseRoom,
  onPackage,
  onRegisterInterest,
  onStartReceiptIntake,
  onCorrectedInputs,
  onFeedback,
  onPersonaFeedback
}: {
  report: ReturnType<typeof buildShadowSignalReport>;
  review: DarkRoomReview;
  decisionSummary: ReturnType<typeof buildSignalDecisions>;
  decisionSources: DecisionSourceTrace[];
  projectId: string | null;
  readiness: ReturnType<typeof buildReadinessMap>;
  studioGate: ReturnType<typeof buildStudioGateBrief>;
  actionPlan: ReturnType<typeof buildShadowActionPlan>;
  signalCapsule: ReturnType<typeof buildSignalCapsule>;
  localRunRecord: LocalRunRecord | null;
  personaConfidence: { high: number; medium: number; low: number; total: number; mediumOrAbove: number };
  intakeArtistName: string;
  artistIntake: ArtistIntake;
  onReleaseRoom: () => void;
  onPackage: () => void;
  onCorrectedInputs: (inputs: ArtistIntake) => void;
  onRegisterInterest: (tier: string) => void;
  onStartReceiptIntake: (receipt: ShadowSignalCapsuleReceipt) => void;
  onFeedback: (verdict: "useful" | "not_useful") => void;
  onPersonaFeedback: (personaSlug: string, verdict: "up" | "down") => void;
}) {
  const [capsuleStatus, setCapsuleStatus] = useState<"idle" | "copied" | "downloaded" | "error">("idle");
  const [pitchDraftStatus, setPitchDraftStatus] = useState<"idle" | "copied">("idle");
  const [receiptText, setReceiptText] = useState("");
  const [receiptStatus, setReceiptStatus] = useState<"idle" | "accepted" | "rejected">("idle");
  const [receiptSummary, setReceiptSummary] = useState<ShadowReceiptContinuitySummary | null>(null);
  const [importedReceipt, setImportedReceipt] = useState<ShadowSignalCapsuleReceipt | null>(null);
  const [freshIntakeConfirmation, setFreshIntakeConfirmation] = useState(false);
  const [deliveryEmail, setDeliveryEmail] = useState(artistIntake.email);
  const [emailDeliveryStatus, setEmailDeliveryStatus] = useState<"idle" | "sending" | "sent" | "error" | "delivery_disabled">("idle");
  const [emailDeliveryMessage, setEmailDeliveryMessage] = useState("");
  const [guideVisible, setGuideVisible] = useState(() => !loadPreference("signalGuideShown"));
  const [signalTab, setSignalTab] = useState<"report" | "actions" | "capsule" | "history">(() =>
    (loadPreference("shadow-signal-tab") as "report" | "actions" | "capsule" | "history") ?? "report"
  );
  const [editedHeadline, setEditedHeadline] = useState(report.seamlineDraft.headline);
  const [editedBio, setEditedBio] = useState(report.seamlineDraft.bioLong);
  const [seamlineCopyStatus, setSeamlineCopyStatus] = useState<"idle" | "headline" | "bio">("idle");
  const originalHeadline = report.seamlineDraft.headline;
  const originalBio = report.seamlineDraft.bioLong;
  const [feedbackVote, setFeedbackVote] = useState<"useful" | "not_useful" | null>(null);
  const [expandedPersonas, setExpandedPersonas] = useState<Set<string>>(new Set());
  const [personaFeedback, setPersonaFeedback] = useState<Record<string, "up" | "down">>({});
  const signalHistory = useMemo<Array<{ ts: number; artistName: string; projectId?: string; assessment?: AssessmentEnvelope; readinessScore: number; overallScore: number; personaScores?: Record<string, number>; openGates?: string[] }>>(() => {
    const all = loadJson<Array<{ ts: number; artistName: string; projectId?: string; assessment?: AssessmentEnvelope; readinessScore: number; overallScore: number; personaScores?: Record<string, number>; openGates?: string[] }>>("shadow-signal-history", []);
    return all.filter((e) => (projectId && e.projectId === projectId) || (!e.projectId && e.artistName === report.header.artistName));
  }, [projectId, report.header.artistName, report.assessment?.generatedAt]);
  const comparableHistory = useMemo(() => projectId ? comparableAssessmentSuffix(signalHistory.filter(entry => entry.projectId === projectId), report.assessment) : [], [signalHistory, report.assessment, projectId]);
  const [liveGenreStats, setLiveGenreStats] = useState<Record<string, { avgReadiness: number; sampleSize: number; source: "real" | "estimated" }>>({});
  const [deepDivePersona, setDeepDivePersona] = useState<typeof report.personaCards[0] | null>(null);
  const signalReceipt = useMemo(() => buildSignalCapsuleReceipt(signalCapsule), [signalCapsule]);
  const capsuleText = useMemo(() => renderSignalCapsule(signalCapsule), [signalCapsule]);
  const personaTensions = useMemo(() => computePersonaTensions(report.personaCards), [report.personaCards]);
  const personaGenreDeltas = useMemo(
    () => computePersonaGenreDeltas(report.personaCards, readiness.genreBenchmark),
    [report.personaCards, readiness.genreBenchmark]
  );
  const readinessVelocity = useMemo(() => computeReadinessVelocity(comparableHistory), [comparableHistory]);
  const priorPersonaScores = useMemo<Record<string, number> | null>(
    () => comparableHistory.length >= 2 ? (comparableHistory[comparableHistory.length - 2].personaScores ?? null) : null,
    [comparableHistory]
  );
  const artistFingerprintSvg = useMemo(
    () => generateArtistFingerprint(report.header.artistName, report.scores),
    [report.header.artistName, report.scores]
  );

  useEffect(() => {
    let cancelled = false;
    fetch("/api/shadow/genre-stats")
      .then((r) => r.ok ? r.json() as Promise<{ source: string; stats: typeof liveGenreStats }> : null)
      .then((data) => { if (!cancelled && data?.stats) setLiveGenreStats(data.stats); })
      .catch(() => { /* fall back to static */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!deliveryEmail && artistIntake.email) setDeliveryEmail(artistIntake.email);
  }, [artistIntake.email, deliveryEmail]);

  async function copyCapsule() {
    try {
      await navigator.clipboard.writeText(capsuleText);
      setCapsuleStatus("copied");
    } catch {
      setCapsuleStatus("error");
    }
  }

  function downloadCapsule() {
    const blob = new Blob([JSON.stringify(signalReceipt, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${signalCapsule.artistName.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "shadow"}-signal-receipt.json`;
    link.click();
    URL.revokeObjectURL(url);
    setCapsuleStatus("downloaded");
  }

  function importReceipt() {
    const result = parseSignalReceipt(receiptText);
    setFreshIntakeConfirmation(false);
    if (result.valid) {
      setImportedReceipt(result.receipt);
      setReceiptSummary(summarizeSignalReceipt(result.receipt));
      setReceiptStatus("accepted");
    } else {
      setImportedReceipt(null);
      setReceiptSummary(null);
      setReceiptStatus("rejected");
    }
  }

  async function emailReceipt() {
    if (!deliveryEmail.trim()) return;
    setEmailDeliveryStatus("sending");
    setEmailDeliveryMessage("");
    try {
      const response = await fetch("/api/shadow/signal-email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: deliveryEmail.trim(),
          name: artistIntake.artistName || intakeArtistName,
          receipt: signalReceipt
        })
      });
      const body = await readJsonSafe<DeliveryApiResponse>(response);
      if (response.ok) {
        setEmailDeliveryStatus("sent");
        setEmailDeliveryMessage(`Receipt emailed to ${body?.toEmail ?? deliveryEmail.trim()}.`);
        return;
      }
      if (body?.error === "delivery_unavailable") {
        setEmailDeliveryStatus("delivery_disabled");
        setEmailDeliveryMessage(body.message ?? "Signal email delivery is not active yet. Download the receipt instead.");
        return;
      }
      setEmailDeliveryStatus("error");
      setEmailDeliveryMessage(body?.message ?? "Signal receipt email failed. Download the receipt instead.");
    } catch {
      setEmailDeliveryStatus("error");
      setEmailDeliveryMessage("Signal receipt email failed. Download the receipt instead.");
    }
  }

  function switchTab(tab: "report" | "actions" | "capsule" | "history") {
    savePreference("shadow-signal-tab", tab);
    setSignalTab(tab);
  }

  function dismissGuide() {
    savePreference("signalGuideShown", "1");
    setGuideVisible(false);
  }

  function handlePrintSignal() {
    const body = document.body;
    body.classList.add("signalPrintMode");
    const cleanup = () => { body.classList.remove("signalPrintMode"); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    window.print();
  }

  useEffect(() => {
    const header = document.querySelector(".signalStickyHeader");
    const identity = document.querySelector(".artistIdentityPanel");
    if (!header || !identity) return;
    const obs = new IntersectionObserver(
      ([entry]) => header.classList.toggle("stickyVisible", !entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(identity);
    return () => obs.disconnect();
  }, [signalTab]);

  return (
    <section className="workspace reportWorkspace">
      <section className="panel assessmentMethod" aria-label="Assessment method and evidence">
        <p className="panelKicker">How this result was prepared</p>
        <h2>{review.assessment?.mode === "demo" ? "Example report — not your assessment" : (review.assessment?.mode.replaceAll("_", " ") ?? "Unknown legacy method")}</h2>
        <p>Rubric {review.assessment?.rubricVersion ?? "unknown"} · Generated {report.header.date}</p>
        <p>{review.assessment?.coverage.textCharacters ?? "Unknown"} text characters · {review.assessment?.coverage.suppliedLinks ?? "Unknown"} links supplied. No audio examined; links not opened.</p>
        <p>Scores describe preparation inputs and checklist coverage. Musical quality, audience demand, ownership and release success remain unverified.</p>
        <details><summary>Limits and missing evidence</summary>
          <ul>{(review.assessment?.limitations ?? ["Legacy report has no verified assessment provenance. Re-run before relying on it."]).map(limit => <li key={limit}>{limit}</li>)}</ul>
          <p>Missing: {report.missingInfo.join(", ") || "No missing fields in this rubric; independent verification is still required."}</p>
        </details>
      </section>
      <div className="signalStickyHeader" aria-hidden="true">
        <span className="stickyArtistName">{report.header.artistName}</span>
        <span className={`stickyScoreBand band-${readiness.score >= 80 ? "strong" : readiness.score >= 60 ? "mid" : "early"}`}>
          {readiness.score} readiness
        </span>
        <span className="stickyDate">{report.header.date}</span>
      </div>
      {deepDivePersona && (
        <Suspense fallback={null}>
          <LazyPersonaDeepDiveModal
            persona={deepDivePersona}
            feedbackState={personaFeedback[deepDivePersona.persona] ?? null}
            onFeedback={(verdict) => { setPersonaFeedback((prev) => ({ ...prev, [deepDivePersona.persona]: verdict })); onPersonaFeedback(deepDivePersona.persona, verdict); }}
            onClose={() => setDeepDivePersona(null)}
            personaHistory={comparableHistory
              .filter((e) => e.personaScores?.[deepDivePersona.persona] !== undefined)
              .map((e) => ({ score: e.personaScores![deepDivePersona.persona] }))}
          />
        </Suspense>
      )}
      <nav className="signalTabs" role="tablist" aria-label="Signal sections">
        {(["report", "actions", "capsule"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={signalTab === t}
            className={`signalTab ${signalTab === t ? "activeTab" : ""}`}
            onClick={() => switchTab(t)}
          >
            {t === "report" ? "Report" : t === "actions" ? "Actions" : "Capsule"}
          </button>
        ))}
        {(
          <button
            type="button"
            role="tab"
            aria-selected={signalTab === "history"}
            className={`signalTab ${signalTab === "history" ? "activeTab" : ""}`}
            onClick={() => switchTab("history")}
          >
            History
          </button>
        )}
      </nav>
      {signalTab === "report" && <section className="panel report">
        <p className="panelKicker">04 / SHADOW Signal</p>
        <DecisionSummary summary={decisionSummary} sources={decisionSources} onActions={() => switchTab("actions")} />
        {guideVisible && (
          <div className="signalGuideStrip">
            <div className="signalGuideContent">
              <strong>First time here?</strong>
              <span>The dial shows your release readiness score (0–100). Personas below are sorted lowest-score first — those are your sharpest concerns. The action panel on the right has your ranked next moves.</span>
            </div>
            <button type="button" className="secondaryButton signalGuideDismiss" onClick={dismissGuide}>Got it</button>
          </div>
        )}
        {report.header.artistName !== intakeArtistName && intakeArtistName.trim() !== "" && (
          <div className="staleReviewBanner" role="alert">
            Session data was reset — re-run the Dark Room to see your results.
          </div>
        )}
        <div className="artistIdentityPanel" aria-label="Artist identity summary">
          <div className="artistIdentityName">{report.header.artistName}</div>
          <div className="artistFingerprint" dangerouslySetInnerHTML={{ __html: artistFingerprintSvg }} />
          <div className="artistIdentityMeta">
            {artistIntake.genre && <span className="artistIdentityGenre">{artistIntake.genre}</span>}
            {artistIntake.goals.slice(0, 2).map((g) => (
              <span key={g} className="artistIdentityGoal">{g}</span>
            ))}

          </div>

        </div>
        <Suspense fallback={<p role="status">Opening release preparation progress…</p>}><LazyPreparationProgress record={localRunRecord} onActions={() => switchTab("actions")} onPackage={onPackage} onHistory={() => switchTab("history")} /></Suspense>
        {review.assessment?.mode === "human_review" && (() => {
          const archetype = buildArtistArchetypeProfile(review);
          return (
            <div className="artistArchetypeCard">
              <span className="archetypeLabel">Your archetype</span>
              <strong className="archetypeName">{archetype.archetype}</strong>
              <p className="archetypeTagline">{archetype.tagline}</p>
              <div className="archetypeDetails">
                <div className="archetypeStrength"><span>Strength</span>{archetype.strength}</div>
                <div className="archetypeEdge"><span>Growth edge</span>{archetype.growthEdge}</div>
                <div className="archetypeNext"><span>Next move</span>{archetype.nextMove}</div>
              </div>
            </div>
          );
        })()}
        <header className="reportHeader">
          <div>
            <h2>{report.header.artistName}</h2>
            <p>{report.header.reviewType.replaceAll("_", " ")} · confidence {report.header.confidence}</p>
          </div>
          <div className="reportHeaderActions">
            <span>{report.suggestedDepth}</span>
            <button type="button" className="secondaryButton printReportBtn" onClick={handlePrintSignal} aria-label="Print this Signal report">Print report</button>
          </div>
        </header>
        <nav className="reportJumpNav" aria-label="Report sections">
          <a href="#report-scores" className="jumpNavLink">Scores</a>
          <a href="#report-readiness" className="jumpNavLink">Readiness</a>
          <a href="#report-personas" className="jumpNavLink">Personas</a>
          <a href="#report-voice" className="jumpNavLink">SHADOW</a>
        </nav>
        <p className="disclaimer">{report.header.disclaimer}</p>
        <div className="verdict">{report.verdict}</div>
        <div id="report-scores" className="reportScores">
          <RadarChart scores={report.scores} />
          <div className="scores">
            {Object.entries(report.scores).map(([key, value]) => (
              <div key={key} className="scoreCard">
                <span>{key}</span>
                <strong>{Math.round(value)}</strong>
              </div>
            ))}
          </div>
        </div>
        {(() => {
          const anatomy = computeScoreAnatomy(review);
          const total = anatomy.segments.reduce((s, seg) => s + seg.contribution, 0) || 1;
          return (
            <div className="scoreAnatomyPanel" aria-label="Score anatomy breakdown">
              <span className="anatomyLabel">Score anatomy</span>
              <div className="anatomyBar">
                {anatomy.segments.map((seg) => (
                  <div
                    key={seg.label}
                    className="anatomySegment"
                    style={{ width: `${(seg.contribution / total) * 100}%`, background: seg.color }}
                    title={`${seg.label}: ${seg.score} (×${seg.weight} = ${seg.contribution}pts)`}
                  />
                ))}
              </div>
              <div className="anatomyLegend">
                {anatomy.segments.map((seg) => (
                  <span key={seg.label} className="anatomyLegendItem">
                    <span className="anatomyDot" style={{ background: seg.color }} />
                    {seg.label} {seg.score}
                  </span>
                ))}
              </div>
              <div className="anatomyMeta">
                <span>Dominant: <strong>{anatomy.dominantAxis}</strong></span>
                <span>Needs work: <strong>{anatomy.weakestAxis}</strong></span>
              </div>
            </div>
          );
        })()}
        <div className="personaConfidenceSummary">
          <span>{personaConfidence.mediumOrAbove}/{personaConfidence.total} personas confident or above</span>
          <div className="confidenceBar">
            <span className="confHigh" style={{ width: `${(personaConfidence.high / personaConfidence.total) * 100}%` }} title={`High: ${personaConfidence.high}`} />
            <span className="confMedium" style={{ width: `${(personaConfidence.medium / personaConfidence.total) * 100}%` }} title={`Medium: ${personaConfidence.medium}`} />
            <span className="confLow" style={{ width: `${(personaConfidence.low / personaConfidence.total) * 100}%` }} title={`Low: ${personaConfidence.low}`} />
          </div>
          <small>{personaConfidence.high} high · {personaConfidence.medium} medium · {personaConfidence.low} low</small>
        </div>
        {report.personaCards.length > 0 && (() => {
          const sorted = [...report.personaCards].sort((a, b) => a.score - b.score);
          return (
            <div className="personaConsensusHeatmap" aria-label="Persona consensus heatmap" role="img">
              <span className="heatmapLabel">Persona consensus</span>
              <div className="heatmapCells">
                {sorted.map((p) => {
                  const intensity = p.score / 100;
                  const r = Math.round(220 - intensity * 150);
                  const g = Math.round(80 + intensity * 140);
                  const b = Math.round(60 + intensity * 20);
                  return (
                    <div
                      key={p.persona}
                      className="heatmapCell"
                      style={{ background: `rgba(${r},${g},${b},0.82)` }}
                      title={`${p.persona}: ${p.score}`}
                    >
                      <span className="heatmapCellScore">{p.score}</span>
                      <span className="heatmapCellName">{p.persona.split(" ").slice(-1)[0]}</span>
                    </div>
                  );
                })}
              </div>
              {personaTensions.length > 0 && (
                <div className="heatmapTensions">
                  {personaTensions.map((t) => (
                    <span key={t.label} className={`personaTensionChip ${t.type === "consensus_risk" ? "tensionConsensus" : t.type === "divergence" ? "tensionDivergence" : "tensionStrength"}`}>
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        {signalCapsule.shadowVoiceSummary && (
          <blockquote id="report-voice" className="signalVoiceBlock">
            <span className="signalVoicePre">SHADOW</span>
            <p>{signalCapsule.shadowVoiceSummary}</p>
          </blockquote>
        )}
        <div className="personasSectionHeader">
          <span className="personasSectionTitle">Persona verdicts</span>
          <button
            type="button"
            className="secondaryButton downloadAllCardsBtn"
            onClick={() => {
              report.personaCards.forEach((persona, i) => {
                setTimeout(() => {
                  const svg = generatePersonaCard(persona, report.header.artistName);
                  const blob = new Blob([svg], { type: "image/svg+xml" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `shadow-${i + 1}-${persona.persona.toLowerCase().replace(/\s+/g, "-")}.svg`;
                  a.click();
                  URL.revokeObjectURL(url);
                }, i * 120);
              });
            }}
            aria-label="Download all 10 persona cards as SVG files"
          >
            Download all {report.personaCards.length} cards
          </button>
        </div>
        {(() => {
          const matrix = buildAxisConsensusMatrix(review);
          return (
            <div className="axisConsensusMatrix" aria-label="Axis consensus matrix">
              <span className="axisMatrixLabel">Axis consensus</span>
              <div className="axisMatrixRows">
                {matrix.map((row) => (
                  <div key={row.axis} className={`axisMatrixRow agree-${row.agreement}`}>
                    <span className="axisName">{row.axis}</span>
                    <div className="axisHeatBar">
                      <div className="axisHeatFill" style={{ width: `${row.avgScore}%` }} />
                    </div>
                    <span className="axisAvgScore">{row.avgScore}</span>
                    <span className="axisAgreeBadge">{row.agreement}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div id="report-personas" className="personas">
          {(() => {
            const sorted = [...report.personaCards].sort((a, b) => a.score - b.score);
            const nodes: React.ReactNode[] = [];
            let lastBand: string | null = null;
            sorted.forEach((persona, sortIndex) => {
              const band = persona.score >= 80 ? "strength" : persona.score >= 60 ? "developing" : "focus";
              if (band !== lastBand) {
                const label = band === "strength" ? "Strengths" : band === "developing" ? "Developing" : "Focus here";
                nodes.push(<div key={`band-${band}`} className={`personaBandHeader personaBand--${band}`}>{label}</div>);
                lastBand = band;
              }
              const isExpanded = expandedPersonas.has(persona.persona);
              const isLowestThree = sortIndex < 3;
              const traceInsight = "Trace interest only. No additional provider assessment or verified result is included in this local preparation report.";
              const genreDelta = personaGenreDeltas[persona.persona];
              const priorScore = priorPersonaScores?.[persona.persona];
              const scoreDelta = priorScore !== undefined ? persona.score - priorScore : undefined;
              const reactionLine = scoreDelta === undefined ? null : review.assessment?.mode === "human_review"
                ? getPersonaReactionLine(persona.persona, scoreDelta)
                : `Same-rubric coverage changed by ${scoreDelta > 0 ? "+" : ""}${scoreDelta} points. This does not establish a change in musical quality.`;
              nodes.push(
                <article key={persona.persona} className="persona" data-band={band}>
                  <div>
                    <h3>{persona.persona}</h3>
                    <strong>{persona.score}</strong>
                    {scoreDelta !== undefined && (
                      <span className={`personaRunDelta ${scoreDelta > 3 ? "deltaPositive" : scoreDelta < -3 ? "deltaNegative" : "deltaFlat"}`}>
                        {scoreDelta > 0 ? "+" : ""}{scoreDelta}
                      </span>
                    )}
                    {genreDelta !== undefined && (
                      <span className={`personaGenreDelta ${genreDelta >= 0 ? "deltaAbove" : "deltaBelow"}`}>
                        {genreDelta >= 0 ? "+" : ""}{genreDelta} vs genre
                      </span>
                    )}
                  </div>
                  {reactionLine && <p className="personaReactionLine">{reactionLine}</p>}
                  <p>{persona.verdict}</p>
                  {persona.strengths && persona.strengths.length > 0 && (
                    <div className="personaStrengths">
                      {persona.strengths.map((s) => <span key={s} className="strengthPill">{s}</span>)}
                    </div>
                  )}
                  {persona.weaknesses && persona.weaknesses.length > 0 && (
                    <div className="personaWeaknesses">
                      {persona.weaknesses.map((w) => <span key={w} className="weaknessPill">{w}</span>)}
                    </div>
                  )}
                  {isLowestThree && <TracePreviewChip insight={traceInsight} onRegisterInterest={onRegisterInterest} />}
                  {persona.critique && (
                    <>
                      <button
                        type="button"
                        className="personaExpandBtn"
                        onClick={() =>
                          setExpandedPersonas((prev) => {
                            const next = new Set(prev);
                            if (next.has(persona.persona)) next.delete(persona.persona);
                            else next.add(persona.persona);
                            return next;
                          })
                        }
                      >
                        {isExpanded ? "Collapse verdict" : "Read full verdict"}
                      </button>
                      {isExpanded && <p className="personaCritique">{persona.critique}</p>}
                    </>
                  )}
                  {persona.action && <div className="personaAction">{persona.action}</div>}
                  <small className="personaWarning">{persona.warning}</small>
                  <p className="personaSourceStatus">{localRunRecord?.producerSources && JSON.stringify(localRunRecord.run.review)===JSON.stringify(review) ? "Provisional statements with recorded preparation context." : "Source not recorded for these statements."} <a href="#report-sources">Inspect the report trace</a> before treating them as established facts.</p>
                  <button type="button" className="personaDeepDiveBtn secondaryButton" onClick={() => setDeepDivePersona(persona)}>Deep dive</button>
                  <div className="personaFeedbackRow">
                    <span>Helpful?</span>
                    <button
                      type="button"
                      className={`personaThumb ${personaFeedback[persona.persona] === "up" ? "thumbActive" : ""}`}
                      onClick={() => { setPersonaFeedback((prev) => ({ ...prev, [persona.persona]: "up" })); onPersonaFeedback(persona.persona, "up"); }}
                      disabled={!!personaFeedback[persona.persona]}
                      aria-label="Helpful"
                    >👍</button>
                    <button
                      type="button"
                      className={`personaThumb ${personaFeedback[persona.persona] === "down" ? "thumbActive" : ""}`}
                      onClick={() => { setPersonaFeedback((prev) => ({ ...prev, [persona.persona]: "down" })); onPersonaFeedback(persona.persona, "down"); }}
                      disabled={!!personaFeedback[persona.persona]}
                      aria-label="Not helpful"
                    >👎</button>
                    {personaFeedback[persona.persona] && <span className="personaFeedbackThanks">Thanks!</span>}
                    <button
                      type="button"
                      className="personaShareBtn"
                      aria-label={`Share ${persona.persona} card`}
                      onClick={() => {
                        const svg = generatePersonaCard(persona, report.header.artistName);
                        const blob = new Blob([svg], { type: "image/svg+xml" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `shadow-${persona.persona.toLowerCase().replace(/\s+/g, "-")}.svg`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >Share card</button>
                  </div>
                </article>
              );
            });
            return nodes;
          })()}
        </div>
        <Suspense fallback={<p role="status">Opening report source records…</p>}><LazyReportSources review={review} record={localRunRecord} /></Suspense>
      </section>}
      {signalTab === "report" && review.assessment?.mode === "human_review" && <WhatIfPanel currentScores={report.scores} artistIntake={artistIntake} />}
      {(signalTab === "actions" || signalTab === "capsule" || signalTab === "history") && <aside className="panel actionPanel">
        {signalTab === "actions" && <>
        <ProjectActions record={localRunRecord} />
        <details className="projectActionReportContext"><summary>Report context and preparation suggestions</summary>
        <p className="panelKicker">Next best moves</p>
        {receiptStatus === "accepted" && receiptSummary && (() => {
          const scoreDelta = readiness.score - receiptSummary.readinessScore;
          const gatesDelta = receiptSummary.openGateCount - (readiness.milestones?.filter((m) => m.status !== "ready").length ?? 0);
          return (
            <div className={`progressDeltaChip ${scoreDelta >= 0 ? "deltaPositive" : "deltaNegative"}`}>
              <span>Progress since last session</span>
              <strong>{scoreDelta >= 0 ? "+" : ""}{scoreDelta} pts</strong>
              {gatesDelta > 0 && <span>{gatesDelta} gate{gatesDelta !== 1 ? "s" : ""} closed</span>}
            </div>
          );
        })()}
        <ReadinessDial
          readinessScore={readiness.score}
          status={readiness.status}
          fromScore={receiptStatus === "accepted" && receiptSummary ? receiptSummary.readinessScore : undefined}
        />
        {readinessVelocity && (
          <div className={`velocityChip velocityChip--${readinessVelocity.trend}`} aria-label={`Readiness velocity: ${readinessVelocity.label}`}>
            {readinessVelocity.label}
            <span className="velocityChipSessions">over {readinessVelocity.sessions} sessions</span>
          </div>
        )}
        <h2 id="report-readiness">{readiness.nextBestMove}</h2>
        {readiness.genreBenchmark && (() => {
          const genreKey = readiness.genreBenchmark.genre.toLowerCase();
          const liveEntry = liveGenreStats[genreKey];
          const displayAvg = liveEntry?.avgReadiness ?? readiness.genreBenchmark.avgReadiness;
          const source = liveEntry ? `Based on ${liveEntry.sampleSize} session${liveEntry.sampleSize !== 1 ? "s" : ""}` : `${readiness.genreBenchmark.note} benchmark`;
          return (
            <div className="genreBenchmark">
              <span>{readiness.genreBenchmark.genre} avg readiness: {displayAvg}</span>
              <span className={readiness.score >= displayAvg ? "benchmarkAbove" : "benchmarkBelow"}>
                {readiness.score >= displayAvg ? "above" : "below"} average
              </span>
              <small>Top persona in this genre: {readiness.genreBenchmark.topPersona} · {source}</small>
            </div>
          );
        })()}
        <div className={`studioGateBrief ${studioGate.decision}`}>
          <span>Studio Gate brief</span>
          <strong>{studioGate.label}</strong>
          <p>{studioGate.rationale}</p>
          <small>Risk {studioGate.riskLevel} · decision {studioGate.decision}</small>
        </div>
        <details className="reportActionSuggestions"><summary>Evidence requirements from the report</summary>
          <p>Suggested preparation steps remain unverified report context. Track current project work above.</p>
          <ul>{actionPlan.items.map(item=><li key={item.id}><strong>{item.title}</strong><p>{item.proof}</p></li>)}</ul>
        </details>

        <div className="seamlineDraftPanel">
          <span>Seamline Draft</span>
          <p>Your public artist story, ready to refine. Edits are local-only — copy and use anywhere.</p>
          <label className="seamlineField">
            Headline
            <textarea
              value={editedHeadline}
              onChange={(e) => setEditedHeadline(e.target.value)}
              rows={2}
              maxLength={200}
            />
            <div className="seamlineActions">
              <button
                type="button"
                className="secondaryButton"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(editedHeadline); setSeamlineCopyStatus("headline"); }
                  catch { /* clipboard unavailable */ }
                }}
              >
                {seamlineCopyStatus === "headline" ? "Copied!" : "Copy headline"}
              </button>
              <small>{editedHeadline.length}/200</small>
              {editedHeadline !== originalHeadline && (
                <button type="button" className="seamlineRevertBtn" onClick={() => setEditedHeadline(originalHeadline)}>
                  Revert to original
                </button>
              )}
            </div>
          </label>
          <label className="seamlineField">
            Bio
            <textarea
              value={editedBio}
              onChange={(e) => setEditedBio(e.target.value)}
              rows={5}
              maxLength={800}
            />
            <div className="seamlineActions">
              <button
                type="button"
                className="secondaryButton"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(editedBio); setSeamlineCopyStatus("bio"); }
                  catch { /* clipboard unavailable */ }
                }}
              >
                {seamlineCopyStatus === "bio" ? "Copied!" : "Copy bio"}
              </button>
              <small>{editedBio.length}/800</small>
              {editedBio !== originalBio && (
                <button type="button" className="seamlineRevertBtn" onClick={() => setEditedBio(originalBio)}>
                  Revert to original
                </button>
              )}
            </div>
          </label>
          <button type="button" className="secondaryButton seamlineRefineLink" onClick={onPackage}>
            Refine in Package step →
          </button>
        </div>
        </details>
        </>}
        {signalTab === "capsule" && <div className="signalCapsule">
          <span>Signal Capsule</span>
          <p>A portable, artist-owned summary for collaborators, managers, or a future SHADOW return session.</p>
          <pre>{capsuleText}</pre>
          <small>Receipt {signalReceipt.receiptId} · signing {signalReceipt.signature.status.replaceAll("_", " ")}</small>
          <div className="capsuleActions">
            <button type="button" onClick={copyCapsule}>Copy capsule</button>
            <button type="button" className="secondaryButton" onClick={downloadCapsule}>Download receipt</button>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => {
                try {
                  const token = encodeSharedReceiptToken(signalReceipt);
                  window.open(`/share#${token}`, "_blank", "noopener,noreferrer");
                } catch {
                  setCapsuleStatus("error");
                }
              }}
            >
              Share Signal
            </button>
            <button
              type="button"
              className="secondaryButton"
              onClick={async () => {
                const draft = buildSignalDigestDraft(review, readiness, editedHeadline, editedBio);
                try {
                  await navigator.clipboard.writeText(draft);
                  setPitchDraftStatus("copied");
                  setTimeout(() => setPitchDraftStatus("idle"), 2000);
                } catch { /* clipboard unavailable */ }
              }}
            >
              {pitchDraftStatus === "copied" ? "Copied!" : "Copy pitch draft"}
            </button>
          </div>
          {capsuleStatus !== "idle" && (
            <small className={capsuleStatus === "error" ? "statusError" : "statusOk"}>
              {capsuleStatus === "error" ? "Clipboard unavailable. Download still works." : `Capsule ${capsuleStatus}.`}
            </small>
          )}
          <div className="capsuleDelivery">
            <label>
              Email this receipt
              <input
                type="email"
                value={deliveryEmail}
                onChange={(event) => setDeliveryEmail(event.target.value)}
                placeholder="artist@example.com"
                autoComplete="email"
              />
            </label>
            <div className="capsuleDeliveryActions">
              <button
                type="button"
                className="secondaryButton"
                onClick={emailReceipt}
                disabled={emailDeliveryStatus === "sending" || !deliveryEmail.trim()}
              >
                {emailDeliveryStatus === "sending" ? "Sending..." : "Email receipt"}
              </button>
              <small>SHADOW only reports delivery when the live email channel is actually active.</small>
            </div>
            {emailDeliveryStatus === "sent" && <small className="statusOk">{emailDeliveryMessage}</small>}
            {emailDeliveryStatus === "delivery_disabled" && <small className="statusError">{emailDeliveryMessage}</small>}
            {emailDeliveryStatus === "error" && <small className="statusError">{emailDeliveryMessage}</small>}
          </div>
          <div className="receiptImport">
            <label>
              Return with a receipt
              <textarea
                value={receiptText}
                maxLength={128 * 1024}
                onChange={(event) => {
                  setReceiptText(event.target.value);
                  setReceiptSummary(null);
                  setImportedReceipt(null);
                  setReceiptStatus("idle");
                  setFreshIntakeConfirmation(false);
                }}
                placeholder="Paste a SHADOW Signal receipt JSON file..."
              />
            </label>
            <button type="button" className="secondaryButton" onClick={importReceipt} disabled={!receiptText.trim()}>
              Validate receipt
            </button>
            {receiptStatus === "accepted" && receiptSummary && importedReceipt && (
              <div className="receiptSummary">
                <strong>{receiptSummary.artistName} — receipt shape and checksum valid</strong>
                <span>Readiness {receiptSummary.readinessScore} · {receiptSummary.suggestedDepth} · {receiptSummary.openGateCount} open gates</span>
                <p className="receiptTrustNotice">{RECEIPT_TRUST_STATEMENT}</p>
                <p className="receiptMethod">{importedReceiptMethod(importedReceipt)}. Assessment date: {importedReceipt.capsule.assessment?.generatedAt ?? "unknown"}. Artifact date: {receiptSummary.generatedAt}.</p>
                <button
                  type="button"
                  className="secondaryButton"
                  onClick={() => setFreshIntakeConfirmation(true)}
                >
                  Start a fresh intake with this name
                </button>
                {freshIntakeConfirmation && <div className="receiptFreshConfirmation" role="group" aria-label="Fresh intake confirmation">
                  <p>Erase SHADOW data in this browser and start an intake for {receiptSummary.artistName}? This clears local and session data, including forms, consent, reports, history, actions and account references. Download a local recovery copy from Browser data controls first if you need it. Server records and downloaded files are unaffected.</p>
                  <p>Only the artist name is reused. No receipt score, consent or permission is restored. The new intake stays session-only until you choose browser saves.</p>
                  <button type="button" onClick={() => onStartReceiptIntake(importedReceipt)}>Erase local workspace and start intake</button>
                  <button type="button" className="secondaryButton" onClick={() => setFreshIntakeConfirmation(false)}>Cancel</button>
                </div>}
              </div>
            )}
            {receiptStatus === "rejected" && (
              <small className="statusError">Receipt rejected. Integrity, version, or JSON shape did not match SHADOW's receipt contract.</small>
            )}
          </div>
        </div>}
        {signalTab === "actions" && <>
        {comparableHistory.length >= 2 && (() => {
          const scores = comparableHistory.map((e) => e.readinessScore);
          const minS = Math.min(...scores);
          const maxS = Math.max(...scores);
          const range = Math.max(maxS - minS, 1);
          const W = 80, H = 32, pad = 4;
          const pts = scores.map((s, i) => {
            const x = pad + (i / (scores.length - 1)) * (W - pad * 2);
            const y = H - pad - ((s - minS) / range) * (H - pad * 2);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(" ");
          const trend = scores[scores.length - 1] >= scores[0] ? "up" : "down";
          return (
            <div className="progressTimelineSection">
              <div className="progressSparkline">
                <svg width={W} height={H} className="progressSparklineSvg" viewBox={`0 0 ${W} ${H}`}>
                  <polyline points={pts} fill="none" stroke="var(--accent-alt)" strokeWidth="1.5" strokeLinejoin="round" />
                  {scores.map((s, i) => {
                    const x = pad + (i / (scores.length - 1)) * (W - pad * 2);
                    const y = H - pad - ((s - minS) / range) * (H - pad * 2);
                    return <circle key={i} cx={x} cy={y} r={i === scores.length - 1 ? 3 : 2} fill="var(--accent-alt)" />;
                  })}
                </svg>
                <div className="progressSparklineLabel">
                  <strong>{scores[scores.length - 1]} readiness</strong>
                  <span> {trend === "up" ? "↑ higher coverage" : "↓ lower coverage"} over {comparableHistory.length} compatible runs</span>
                </div>
              </div>
            </div>
          );
        })()}
        <details className="additionalPreparation">
          <summary>Additional preparation suggestions</summary>
          <p>These report suggestions are context, not tracked or reviewed project completion.</p>
          <ul>{report.allNextActions.map((action,index)=><li key={index}>{action}</li>)}</ul>
        </details>
        <DepthPath suggestedDepth={report.suggestedDepth} onRegisterInterest={onRegisterInterest} />
        <div className="formActions splitActions">
          <button type="button" onClick={onReleaseRoom}>Open Release Room</button>
          <button type="button" className="secondaryButton" onClick={onPackage}>Build package</button>
        </div>
        </>}
        {signalTab === "capsule" && <div className="feedbackWidget">
          {feedbackVote === null ? (
            <>
              <span>Was this Signal report useful?</span>
              <div className="feedbackButtons">
                <button
                  type="button"
                  className="feedbackBtn"
                  aria-label="Useful"
                  onClick={() => { setFeedbackVote("useful"); onFeedback("useful"); }}
                >
                  👍
                </button>
                <button
                  type="button"
                  className="feedbackBtn"
                  aria-label="Not useful"
                  onClick={() => { setFeedbackVote("not_useful"); onFeedback("not_useful"); }}
                >
                  👎
                </button>
              </div>
            </>
          ) : (
            <span className="feedbackConfirm">Thanks for the feedback.</span>
          )}
        </div>}
        {signalTab === "history" && <Suspense fallback={<p role="status">Opening project evidence history…</p>}><LazyProjectHistory key={localRunRecord?.run.references.runId ?? "legacy"} record={localRunRecord} onCorrectedInputs={onCorrectedInputs} /></Suspense>}
      </aside>}
    </section>
  );
}

function StatusBoard({ route, readiness }: { route: ReturnType<typeof routeDarkRoomLite>; readiness: ReturnType<typeof buildReadinessMap> }) {
  return (
    <article className="panel statusBoard">
      <p className="panelKicker">Current operating state</p>
      <div className="budgetGrid">
        <div><span>Analysis</span><strong>{route.allowed ? "Ready" : "Blocked"}</strong></div>
        <div><span>Readiness</span><strong>{readiness.score}%</strong></div>
        <div><span>Free cap</span><strong>${route.costLedger.maxCostUsd.toFixed(2)}</strong></div>
        <div><span>Cache</span><strong>{route.costLedger.cacheEligible ? "eligible" : "off"}</strong></div>
      </div>
    </article>
  );
}

function DepthPath({ suggestedDepth, onRegisterInterest }: { suggestedDepth: string; onRegisterInterest: (tier: string) => void }) {
  const recommended = recommendedDepthIndex(suggestedDepth);
  return (
    <div className="depthPath">
      <p className="panelKicker">Depth path</p>
      {depthTiers.map((tier, index) => (
        <article key={tier.id} className={index <= recommended ? "depthTier recommended" : "depthTier"}>
          <span>{tier.label}</span>
          <p>{tier.fit}</p>
          <small>{tier.boundary}</small>
          <button type="button" className="secondaryButton" onClick={() => onRegisterInterest(tier.id)}>
            Register interest
          </button>
        </article>
      ))}
    </div>
  );
}

function WaitlistModal({
  tier,
  artistName,
  artistEmail,
  onClose
}: {
  tier: string;
  artistName: string;
  artistEmail: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(artistEmail);
  const [name, setName] = useState(artistName);
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error" | "delivery_disabled">("idle");
  const [message, setMessage] = useState("");

  async function submit() {
    if (!email.trim()) return;
    setStatus("submitting");
    setMessage("");
    try {
      const res = await fetch("/api/shadow/waitlist", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), tier })
      });
      const body = await readJsonSafe<DeliveryApiResponse>(res);
      if (!res.ok) {
        if (body?.error === "delivery_unavailable") {
          setStatus("delivery_disabled");
          setMessage(body.message ?? "Waitlist delivery is not active right now. Use contact@yourshadow.io instead.");
          return;
        }
        throw new Error(body?.message ?? "waitlist_failed");
      }
      setStatus("done");
    } catch {
      setStatus("error");
      setMessage("Waitlist request failed. Try again or email contact@yourshadow.io directly.");
    }
  }

  return (
    <div
      className="modalOverlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={`Register interest in ${tier}`}>
        {status === "done" ? (
          <>
            <p className="panelKicker">Registered</p>
            <h2>You're on the list.</h2>
            <p style={{ color: "var(--text-muted)" }}>
              We'll reach out when {tier} opens. One email — no campaigns.
            </p>
            <button type="button" onClick={onClose}>Close</button>
          </>
        ) : status === "delivery_disabled" ? (
          <>
            <p className="panelKicker">Delivery disabled</p>
            <h2>Waitlist delivery is not live yet.</h2>
            <p style={{ color: "var(--text-muted)" }}>{message}</p>
            <div className="modalActions">
              <a className="secondaryButton modalLinkButton" href="mailto:contact@yourshadow.io">Email contact@yourshadow.io</a>
              <button type="button" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="panelKicker">Depth path</p>
            <h2>Register interest in {tier}</h2>
            <p style={{ color: "var(--text-muted)" }}>
              Leave the best email for launch access. SHADOW will only use it for tier availability.
            </p>
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Artist name" />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="artist@example.com" />
            </label>
            <div className="modalActions">
              <button type="button" className="secondaryButton" onClick={onClose}>Cancel</button>
              <button
                type="button"
                onClick={submit}
                disabled={status === "submitting" || !email.trim()}
              >
                {status === "submitting" ? "Registering..." : "Register"}
              </button>
            </div>
            {status === "error" && <small className="statusError">{message}</small>}
          </>
        )}
      </div>
    </div>
  );
}

function adminQueueItemFromSnapshot({
  review,
  report,
  releaseRoomStatus,
  studioGate,
  email
}: {
  review: DarkRoomReview;
  report: ReturnType<typeof buildShadowSignalReport>;
  releaseRoomStatus: ReturnType<typeof buildReadinessMap>["status"];
  studioGate: ReturnType<typeof buildStudioGateBrief>;
  email: string;
}): AdminQueueItem {
  return {
    artistId: review.artistId,
    reviewId: review.id,
    artistName: review.artistName,
    email,
    overallScore: report.scores.overall,
    confidence: review.confidence,
    suggestedDepth: review.recommendation.suggestedDepth,
    riskFlags: review.recommendation.escalationFlags,
    missingInfo: report.missingInfo,
    releaseRoomStatus,
    studioGateDecision: studioGate.decision,
    studioGateRiskLevel: studioGate.riskLevel,
    requestedAction: releaseRoomStatus === "blocked" ? "request_info" : "invite_upgrade",
    status: "new",
    updatedAt: new Date().toISOString()
  };
}

function renderSignalCapsule(capsule: ReturnType<typeof buildSignalCapsule>): string {
  const openGates = capsule.readiness.openGates.length ? capsule.readiness.openGates : ["No open gates in this Signal pass."];
  return [
    `SHADOW Signal Capsule — ${capsule.artistName}`,
    `Method: ${capsule.assessment?.mode ?? "unknown legacy method"}; rubric: ${capsule.assessment?.rubricVersion ?? "unknown"}; generated: ${capsule.assessment?.generatedAt ?? "unknown"}`,
    "No audio examined; links not opened; ownership and audience demand unverified.",
    `Missing evidence: ${capsule.assessment?.missingEvidence?.join(", ") || (capsule.assessment?.missingEvidence ? "No missing fields in this rubric; independent verification still required" : "Unknown in this legacy assessment")}`,
    `Readiness: ${capsule.readiness.score} (${capsule.readiness.status.replaceAll("_", " ")})`,
    `Suggested depth: ${capsule.suggestedDepth} · Confidence: ${capsule.confidence}`,
    `Verdict: ${capsule.verdict}`,
    `Studio Gate: ${capsule.studioGate.label} · ${capsule.studioGate.decision} · risk ${capsule.studioGate.riskLevel}`,
    `Gate path: ${capsule.studioGate.escalationPath}`,
    "",
    "Next actions:",
    ...capsule.nextActions.map((action) => `- ${action}`),
    "",
    "Ranked action plan:",
    ...capsule.actionPlan.items.map(
      (item, index) => `${index + 1}. [${item.urgency}] ${item.title} - proof: ${item.proof}`
    ),
    "",
    "Open gates:",
    ...openGates.map((gate) => `- ${gate}`),
    "",
    "Required proof:",
    ...capsule.studioGate.requiredProof.map((proof) => `- ${proof}`),
    "",
    `Seamline: ${capsule.seamline.headline}`,
    "",
    "Boundaries:",
    ...capsule.boundaries.map((boundary) => `- ${boundary}`)
  ].join("\n");
}

function tokenFingerprint(token: string): string {
  const compact = token.trim();
  if (compact.length <= 8) return "short token";
  return `${compact.slice(0, 4)}...${compact.slice(-4)}`;
}

function pathToView(pathname: string): View {
  for (const [view, path] of Object.entries(routeManifest.views)) {
    if (view !== "share" && pathname === path) return view as View;
  }
  return pathname.startsWith(routeManifest.views.share) ? "share" : "app";
}

function loadObeliskTransitionSession(): ObeliskTransitionSession | null {
  try {
    const stored = window.sessionStorage.getItem(OBELISK_TRANSITION_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as ObeliskTransitionSession;
    if (parsed?.verified && parsed.adminAccess === false && parsed.authBoundary) return parsed;
    return null;
  } catch {
    return null;
  }
}

function initialTheme(): Theme {
  const stored = loadPreference("shadow-theme");
  if (stored === "void") return "eclipse";
  if (stored === "dark" || stored === "light" || stored === "eclipse") return stored;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function loadJourneyStep(): JourneyStep {
  const stored = loadPreference("shadow-journey-step");
  return journeySteps.some((item) => item.id === stored) ? (stored as JourneyStep) : "landing";
}
